import { Link } from "@tanstack/react-router";
import * as React from "react";

export interface RestaurantCardProps {
  imageSrc: string;
  imageAlt: string;
  title: string | React.ReactNode;
  href?: string;
}

/**
 * RestaurantCard - A reusable card component for displaying restaurant/food items
 *
 * Features:
 * - Image display with hover scale animation and dark overlay
 * - Flexible title support (string or JSX for line breaks)
 * - Optional navigation with TanStack Router Link
 * - Cursor not-allowed when not clickable
 * - Consistent styling with Tennis Gourmet design system
 * - Full accessibility support with proper focus states
 */
export function RestaurantCard({ imageSrc, imageAlt, title, href }: RestaurantCardProps) {
  const cardContent = (
    <div className={`tg-restaurant-card ${!href ? "cursor-not-allowed" : ""}`}>
      <div className="tg-restaurant-card__image-container relative overflow-hidden">
        <img
          src={imageSrc}
          alt={imageAlt}
          className={`tg-restaurant-card__image ${!href ? "" : "xl:group-hover:scale-110"}`}
        />
        {href && (
          <div className="pointer-events-none absolute inset-0 rounded-4xl bg-black opacity-0 transition-all duration-300 ease-out xl:group-hover:opacity-50" />
        )}
      </div>
      <p className="tg-restaurant-card__title tg-h4 text-center">{title}</p>
    </div>
  );

  // If href is provided, wrap in Link component
  if (href) {
    return (
      <Link
        to={href}
        className="tg-restaurant-card__link group focus-visible:outline-none"
      >
        {cardContent}
      </Link>
    );
  }

  // Return unwrapped content for non-clickable cards with group class for hover effects
  return <div className="group">{cardContent}</div>;
}
